/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Trophy, Copy, Check, Mail } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { useGameweeks } from "@/hooks/api/useGameweeks";
import { toast } from "react-toastify";

interface Winner {
  rank: number;
  userId: number;
  userName: string;
  username: string | null;
  email: string;
  avatar: string | null;
  teamName: string;
  formation: string;
  gameweekPoints: number;
  totalPoints: number;
}

interface WinnersResponse {
  gameweek: { id: number; name: string; phase: string } | null;
  winners: Winner[];
}

const MEDAL: Record<number, { emoji: string; bg: string; text: string; border: string }> = {
  1: { emoji: "🥇", bg: "bg-yellow-50", text: "text-yellow-800", border: "border-yellow-300" },
  2: { emoji: "🥈", bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-300" },
  3: { emoji: "🥉", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-300" },
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-gray-100 transition"
      title="Copy"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5 text-gray-400" />}
    </button>
  );
}

function AvatarFallback({ name, avatar }: { name: string; avatar: string | null }) {
  const initials = name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
  if (avatar) return <img src={avatar} className="w-10 h-10 rounded-full object-cover" alt={name} />;
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold">
      {initials}
    </div>
  );
}

export default function GameweekWinnersPage() {
  const [selectedGameweekId, setSelectedGameweekId] = useState<number | null>(null);

  const { data: gameweeksData } = useGameweeks();
  const gameweeks = Array.isArray(gameweeksData) ? gameweeksData : [];

  const { data: winnersData, isLoading } = useQuery<WinnersResponse>({
    queryKey: ["gameweek-winners", selectedGameweekId],
    queryFn: () => apiClient.get(API_ENDPOINTS.FANTASYLEADERBOARD.GAMEWEEK_WINNERS(selectedGameweekId!)),
    enabled: !!selectedGameweekId,
  });

  const winners = winnersData?.winners ?? [];
  const podium = winners.slice(0, 3);
  const rest = winners.slice(3);

  const handleCopyAll = () => {
    const text = winners
      .map((w) => `#${w.rank} ${w.userName} (${w.email}) — ${w.gameweekPoints}pts`)
      .join("\n");
    navigator.clipboard.writeText(text);
    toast.success("All contacts copied!");
  };

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="h-8 w-8 text-yellow-500" />
            Gameweek Winners
          </h1>
          <p className="text-gray-500 mt-1">
            Top 10 per gameweek with contact info for giveaway selection
          </p>
        </div>
        {winners.length > 0 && (
          <Button variant="outline" onClick={handleCopyAll} className="border-blue-300 text-blue-700 hover:bg-blue-50">
            <Copy className="h-4 w-4 mr-2" />
            Copy All Contacts
          </Button>
        )}
      </div>

      {/* Gameweek selector */}
      <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
        <Select
          value={selectedGameweekId?.toString() ?? ""}
          onValueChange={(v) => setSelectedGameweekId(Number(v))}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select a gameweek…" />
          </SelectTrigger>
          <SelectContent>
            {gameweeks.map((gw: any) => (
              <SelectItem key={gw.id} value={String(gw.id)}>
                {gw.name}
                <span className="ml-2 text-xs text-gray-400">({gw.status})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedGameweekId && (
        <div className="text-center py-20 text-gray-400">
          <Trophy className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Select a gameweek to see winners</p>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500" />
        </div>
      )}

      {selectedGameweekId && !isLoading && winners.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <p>No points recorded for this gameweek yet.</p>
        </div>
      )}

      {winners.length > 0 && (
        <div className="space-y-6">
          {/* Podium — top 3 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {podium.map((w) => {
              const medal = MEDAL[w.rank] ?? MEDAL[3];
              return (
                <div key={w.userId} className={`rounded-xl border-2 p-5 ${medal.bg} ${medal.border}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{medal.emoji}</span>
                    <div>
                      <p className={`text-lg font-black ${medal.text}`}>#{w.rank}</p>
                      <p className="text-xs text-gray-500">{w.gameweekPoints} pts this week</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <AvatarFallback name={w.userName} avatar={w.avatar} />
                    <div>
                      <p className="font-bold text-gray-900">{w.userName}</p>
                      {w.username && <p className="text-xs text-gray-400">@{w.username}</p>}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{w.teamName}</p>
                  <Badge variant="outline" className="text-xs mb-3">{w.formation}</Badge>
                  <div className="flex items-center gap-1 bg-white/70 rounded-lg px-2 py-1.5 border">
                    <Mail className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="text-xs text-gray-700 truncate flex-1">{w.email}</span>
                    <CopyButton value={w.email} />
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-right">
                    Total: {w.totalPoints} pts
                  </p>
                </div>
              );
            })}
          </div>

          {/* Ranks 4–10 */}
          {rest.length > 0 && (
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b">
                <p className="text-sm font-semibold text-gray-700">Runners Up (4th – 10th)</p>
              </div>
              <div className="divide-y">
                {rest.map((w) => (
                  <div key={w.userId} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
                    <span className="w-6 text-sm font-bold text-gray-400">#{w.rank}</span>
                    <AvatarFallback name={w.userName} avatar={w.avatar} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{w.userName}</p>
                      <p className="text-xs text-gray-400">{w.teamName}</p>
                    </div>
                    <span className="text-sm font-bold text-blue-600 w-16 text-right">
                      {w.gameweekPoints} pts
                    </span>
                    <div className="flex items-center gap-1 bg-gray-50 rounded px-2 py-1 border w-52">
                      <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="text-xs text-gray-600 truncate flex-1">{w.email}</span>
                      <CopyButton value={w.email} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Giveaway tip */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            <p className="font-semibold mb-1">🎁 Giveaway Checklist</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600 text-xs">
              <li>Copy the winner emails using the buttons above</li>
              <li>Contact them via email or DM to collect their delivery details</li>
              <li>Announce the winners publicly on your social media with their username</li>
              <li>Screenshot this page for transparency if needed</li>
            </ul>
          </div>
        </div>
      )}
    </Layout>
  );
}
